import { isDeadObservation, normalizeRing, uppercaseText } from './domain'
import type { BirdSearchResult, ManualBirdStatus } from './types'

export type BirdStatusKey = 'dead' | 'gifted' | 'home' | 'lost' | 'pending'
export type BirdStatus = { key: BirdStatusKey; label: 'MUERTO' | 'REGALADO' | 'CASA' | 'PERDIDO' | 'PENDIENTE DE REVISIÓN'; reason: string }

const manualLabels: Record<ManualBirdStatus, BirdStatus['label']> = {
  dead: 'MUERTO', gifted: 'REGALADO', home: 'CASA', lost: 'PERDIDO'
}

const descriptiveWords = new Set([
  'MACHERO', 'MACHERA', 'MACHO', 'HEMBRA', 'ENFERMO', 'ENFERMA', 'HERIDO', 'HERIDA',
  'PERDIDO', 'PERDIDA', 'REPRODUCTOR', 'REPRODUCTORA', 'CRIA', 'PENDIENTE', 'DUDOSO',
  'DUDOSA', 'MUERTO', 'MUERTA', 'JAULA', 'GAVIA', 'CASA', 'RESERVA', 'BUENO', 'BUENA',
  'MALO', 'MALA', 'HIJO', 'HIJA', 'ANILLA', 'ROTA', 'ROTO', 'NO', 'SI', 'VUELA',
])

function plain(value: string) {
  return uppercaseText(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim()
}

function looksLikePersonName(value: string) {
  const cleaned = plain(value).replace(/\b(?:PARA|A|DE)\b/g, ' ').replace(/\s+/g, ' ').trim()
  if (!cleaned || !/^[A-ZÑ' -]+$/.test(cleaned)) return false
  const words = cleaned.split(' ').filter(Boolean)
  return words.length <= 4 && words.every(word => !descriptiveWords.has(word))
}

function classifyObservation(value: string): BirdStatusKey {
  const observation = plain(value)
  if (isDeadObservation(observation)) return 'dead'
  if (/\bPERDID[OA]\b/.test(observation)) return 'lost'
  if (!observation || /\d/.test(observation) || /\b(?:JAULA|GAVIA)\b/.test(observation)) return 'home'
  if (/\b(?:REGALAD[OA]?|REGIONAL)\b/.test(observation) || looksLikePersonName(observation)) return 'gifted'
  return 'pending'
}

export function classifyBirdStatus(bird: BirdSearchResult): BirdStatus {
  const observations = [...new Set([bird.observaciones || '', ...bird.appearances.map(item => item.observaciones || '')].map(plain))]
  const nonEmpty = observations.filter(Boolean)
  const statuses = (nonEmpty.length ? nonEmpty : ['']).map(classifyObservation)
  if (statuses.includes('dead')) return { key: 'dead', label: 'MUERTO', reason: 'Alguna observación indica que está muerto.' }
  const unique = new Set(statuses)
  if (unique.size > 1 || unique.has('pending')) return { key: 'pending', label: 'PENDIENTE DE REVISIÓN', reason: 'Las observaciones son ambiguas o indican estados diferentes.' }
  if (unique.has('gifted')) return { key: 'gifted', label: 'REGALADO', reason: 'La observación contiene un nombre de persona o indica regalado/regional.' }
  if (unique.has('lost')) return { key: 'lost', label: 'PERDIDO', reason: 'La observación indica perdido o perdida.' }
  return { key: 'home', label: 'CASA', reason: nonEmpty.length ? 'La observación contiene un número, jaula o gavia.' : 'No tiene observaciones.' }
}

export function birdStatusIdentity(bird: Pick<BirdSearchResult, 'id' | 'source' | 'anilla' | 'nombre'>) {
  const identity = normalizeRing(bird.anilla) || `ID:${bird.id}`
  return `${bird.source || 'APP'}|${identity}|${plain(bird.nombre)}`
}

export function resolveBirdStatus(bird: BirdSearchResult, manual?: ManualBirdStatus): BirdStatus {
  const automatic = classifyBirdStatus(bird)
  if (automatic.key === 'dead') return automatic
  if (!manual) return automatic
  return { key: manual, label: manualLabels[manual], reason: 'Estado seleccionado manualmente.' }
}